import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { Chart } from "react-google-charts";
import { useTable, useFilters } from "react-table";

// Base URL dari .env atau default
const API_BASE_URL = process.env.REACT_APP_API_URL || "https://depotaircerdas.com/api";

const MonthlyReport = ({ username }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // **Ambil Token dari Local Storage**
    const getToken = () => {
        const token = localStorage.getItem("token");
        console.log("📌 Token di Local Storage:", token); // Debugging token
        return token ? `Bearer ${token}` : null;
    };

    // **Ambil Data dengan Token**
    const fetchData = useCallback(async () => {
        const token = getToken();
        if (!token) {
            console.warn("🚨 Token tidak ditemukan. Redirect ke login.");
            window.location.href = "/login";
            return;
        }

        console.log("📌 Token yang dikirim di header:", token);

        try {
            const res = await axios.get(`${API_BASE_URL}/reports/${username}/monthly`, {
                headers: { 
                    "Authorization": token,
                    "Content-Type": "application/json"
                }
            });
            console.log("🚀 API Response:", res.data);
            setData(res.data);
        } catch (error) {
            console.error("❌ Error fetching monthly data:", error);
            setError("Gagal mengambil data bulanan.");
        } finally {
            setLoading(false);
        }
    }, [username]);

    // **Fetch Data Saat Komponen Dimuat & Auto-refresh Tiap 1 Menit**
    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 60000);
        return () => clearInterval(interval);
    }, [fetchData]);

    // **Memproses Data**
    const groupedData = useMemo(() => {
        if (!data?.monthly) return [];

        return data.monthly.map(d => ({
            month: new Date(2025, d.month - 1).toLocaleString("id-ID", { month: "long" }), // Konversi angka ke nama bulan
            volume: d.volume ? parseFloat(d.volume).toFixed(2) : "0.00",
            gallons: d.gallons || 0
        })).reverse(); // 🔄 **Balik urutan: Januari di awal, terbaru di akhir**
    }, [data]);


    // **Cek apakah data kosong**
    const isDataEmpty = groupedData.length === 0;

    // **Kolom Tabel**
    const columns = useMemo(() => [
        { Header: "Bulan", accessor: "month" },
        { Header: "Volume (L)", accessor: "volume" },
        { Header: "Total Galon", accessor: "gallons" }
    ], []);

    const tableInstance = useTable({ columns, data: groupedData }, useFilters);
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

    return (
        <div className="report-container">
            <h3 className="report-title">📅 Laporan Bulanan - {username}</h3>
            <button className="refresh-btn" onClick={fetchData}>🔄 Refresh Data</button>

            {loading ? (
                <p className="loading">Memuat data...</p>
            ) : error ? (
                <p className="error-message">{error}</p>
            ) : isDataEmpty ? (
                <p className="error-message">⚠️ Data bulan ini kosong.</p>
            ) : (
                <>
                    {/* **Grafik Volume & Galon** */}
                    <Chart
                        chartType="LineChart"
                        width="100%"
                        height="350px"
                        data={
                            groupedData.length > 0 
                            ? [["Bulan", "Volume (L)", "Galon"], ...groupedData.map(d => [d.month, Number(d.volume), d.gallons])]
                            : [["Bulan", "Volume (L)", "Galon"], ["-", 0, 0]] // Mencegah error jika data kosong
                        }
                        options={{
                            title: "Trend Penggunaan Air (Bulanan)",
                            curveType: "function",
                            legend: { position: "top" },
                            colors: ["#007bff", "#dc3545"],
                            series: {
                                0: { targetAxisIndex: 0 },
                                1: { targetAxisIndex: 1 }
                            },
                            vAxes: {
                                0: { title: "Volume (L)", textStyle: { color: "#007bff" } },
                                1: { title: "Galon", textStyle: { color: "#dc3545" } }
                            }
                        }}
                    />

                    {/* **Tabel Data** */}
                    <table {...getTableProps()} className="dashboard-table">
                        <thead>
                            {headerGroups.map(headerGroup => (
                                <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                                    {headerGroup.headers.map(column => (
                                        <th {...column.getHeaderProps()} key={column.id}>{column.render("Header")}</th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {rows.map(row => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()} key={row.id}>
                                        {row.cells.map(cell => (
                                            <td {...cell.getCellProps()} key={cell.column.id}>{cell.render("Cell")}</td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
};

export default MonthlyReport;
